import { SwipeableDrawer } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fragment } from 'react';
import useDialog from 'src/helpers/hooks/useDialog';
import { AdditionalInsuredActionButton } from 'src/pages/payment-msi/PaymentMsiV2/components/AdditionalInsuredActionButton';
import { CrossIcon } from 'src/pages/payment-msi/PaymentMsiV2/icons/CrossIcon';
import { TrashIcon } from 'src/pages/payment-msi/PaymentMsiV2/icons/TrashIcon';
import { AppTheme } from 'src/themes/muiTheme';

const deleteAdditionalInsuredButtonStyles = {
  dialogRoot: {
    padding: '40px',
    minWidth: '500px',
    borderRadius: '24px',
  },
  drawerRoot: {
    padding: '40px',
    borderRadius: '24px',
  },
};

interface DeleteAdditionalInsuredButtonProps {
  onDelete: (index: number) => void;
  index: number;
}

export function DeleteAdditionalInsuredButton({
  index,
  onDelete,
}: DeleteAdditionalInsuredButtonProps) {
  const {
    handleClose: closeDialog,
    handleOpen: openDialog,
    open: isDialogOpen,
  } = useDialog();

  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  const handleDelete = () => {
    closeDialog();
    onDelete(index);
  };

  return (
    <Fragment>
      <AdditionalInsuredActionButton
        icon={<TrashIcon />}
        onClick={openDialog}
        variant="delete"
      >
        Delete
      </AdditionalInsuredActionButton>
      {isMobile ? (
        <SwipeableDrawer
          onClose={closeDialog}
          onOpen={openDialog}
          open={isDialogOpen}
          anchor="bottom"
          PaperProps={{
            sx: deleteAdditionalInsuredButtonStyles.drawerRoot,
          }}
        >
          <ModalContent onClose={closeDialog} onDelete={handleDelete} />
        </SwipeableDrawer>
      ) : (
        <Dialog
          PaperProps={{
            sx: deleteAdditionalInsuredButtonStyles.dialogRoot,
          }}
          open={isDialogOpen}
          onClose={closeDialog}
        >
          <ModalContent onClose={closeDialog} onDelete={handleDelete} />
        </Dialog>
      )}
    </Fragment>
  );
}

const modalContentStyles = {
  modalHeader: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
      [mobile]: {
        marginBottom: '16px',
        alignItems: 'flex-start',
      },
    };
  },
  modalBody: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      marginBottom: '24px',
      [mobile]: {
        marginBottom: '32px',
      },
    };
  },
  modalFooter: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '32px',
      [mobile]: {
        flexDirection: 'column-reverse',
        gap: '24px',
      },
    };
  },
  deleteButton: {
    backgroundColor: '#FF3030',
    borderColor: '#FF3030',
    '&:hover': {
      borderColor: '#080A2D',
    },
  },
};
interface ModalContentProps {
  onClose: () => void;
  onDelete: () => void;
}
function ModalContent({ onClose, onDelete }: ModalContentProps) {
  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });
  const cancelButtonVariant = isMobile ? 'outlined' : 'text';

  return (
    <Fragment>
      <Box component={'header'} sx={modalContentStyles.modalHeader}>
        <Typography variant="h4">Delete additionally insured</Typography>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </Box>
      <Box component={'main'} sx={modalContentStyles.modalBody}>
        <Typography variant="body1">
          Are you sure you want to delete this additionally insured?
        </Typography>
      </Box>
      <Box component={'footer'} sx={modalContentStyles.modalFooter}>
        <Button variant={cancelButtonVariant} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={modalContentStyles.deleteButton}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Box>
    </Fragment>
  );
}
